/**
 * Captures an HTML element as an image
 * @param {HTMLElement} element - The element to capture
 * @param {Object} options - Capture options
 * @param {string} options.format - Image format (png, jpeg, webp)
 * @param {number} options.quality - Image quality (0-1) for lossy formats
 * @param {string} options.backgroundColor - Background color
 * @param {boolean} options.captureIframe - Whether this is an iframe container
 * @param {HTMLCanvasElement} canvas - The canvas element to use for capturing
 * @returns {Promise<string>} - Promise resolving to the captured image data URL
 */
export const captureElement = (element, options, canvas) => {
  return new Promise((resolve, reject) => {
    try {
      // Default options
      const defaults = {
        format: 'png',
        quality: 0.9,
        backgroundColor: '#ffffff',
        captureIframe: false
      };
      
      // Merge options with defaults
      const settings = { ...defaults, ...options };
      
      // Get element dimensions
      const rect = element.getBoundingClientRect();
      const width = Math.min(rect.width, 2000); // Limit max width to prevent issues
      const height = Math.min(rect.height, 2000); // Limit max height to prevent issues
      
      if (width === 0 || height === 0) {
        reject(new Error('Element has no dimensions'));
        return;
      }
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Get canvas context
      const ctx = canvas.getContext('2d');
      
      // Set background
      ctx.fillStyle = settings.backgroundColor;
      ctx.fillRect(0, 0, width, height);
      
      if (settings.captureIframe) {
        // This is an iframe container - get the iframe
        const iframe = element.querySelector('iframe');
        if (iframe) {
          // We'll use a modified approach for iframe content
          captureIframeContent(iframe, ctx, width, height)
            .then(() => {
              resolveWithDataURL(canvas, settings, resolve);
            })
            .catch((error) => {
              console.error('Error capturing iframe:', error);
              fallbackRendering(ctx, width, height, canvas, settings, resolve);
            });
        } else {
          fallbackRendering(ctx, width, height, canvas, settings, resolve);
        }
      } else {
        // Use standard DOM-to-Canvas approach for regular elements
        try {
          // Create a clean document fragment
          const tempDiv = document.createElement('div');
          tempDiv.style.position = 'absolute';
          tempDiv.style.top = '0';
          tempDiv.style.left = '0';
          tempDiv.style.width = width + 'px';
          tempDiv.style.height = height + 'px';
          tempDiv.style.backgroundColor = settings.backgroundColor;
          tempDiv.style.overflow = 'hidden';
          tempDiv.innerHTML = element.innerHTML;
          
          // Temporarily append to document
          document.body.appendChild(tempDiv);
          
          // Render elements to canvas
          renderElements(tempDiv, ctx);
          
          // Clean up the temporary element
          document.body.removeChild(tempDiv);
          
          // Convert to data URL
          resolveWithDataURL(canvas, settings, resolve);
        } catch (error) {
          console.error('Error rendering element:', error);
          fallbackRendering(ctx, width, height, canvas, settings, resolve);
        }
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Captures iframe content to a canvas
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {Promise<void>}
 */
const captureIframeContent = (iframe, ctx, width, height) => {
  return new Promise((resolve, reject) => {
    try {
      // Create a temporary canvas
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      
      // Create an image from the iframe content
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      
      // Handle load and error
      img.onload = () => {
        // Draw image on canvas
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        tempCtx.drawImage(img, 0, 0);
        
        // Draw the temp canvas on the main canvas, scaled to fit
        ctx.drawImage(tempCanvas, 0, 0, width, height);
        resolve();
      };
      
      img.onerror = (error) => {
        // Try alternative method - take screenshot of the iframe element itself
        html2canvas(iframe, ctx, width, height);
        resolve();
      };
      
      // For srcdoc iframes, we can render them directly
      const iframeWin = iframe.contentWindow;
      if (iframeWin) {
        try {
          // Take a "screenshot" of the iframe content
          html2canvas(iframe.contentDocument.body, ctx, width, height);
          resolve();
        } catch (e) {
          // Fallback to a basic rendering of what we can access
          basicIframeCapture(iframe, ctx);
          resolve();
        }
      } else {
        // Fallback to basic rendering
        basicIframeCapture(iframe, ctx);
        resolve();
      }
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Basic iframe capture for when more advanced methods fail
 * @param {HTMLIFrameElement} iframe - The iframe element
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 */
const basicIframeCapture = (iframe, ctx) => {
  try {
    // Draw a placeholder if we can't access the iframe content
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Try to get something from the iframe if possible
    if (iframe.contentDocument) {
      // Draw text content
      ctx.font = "14px Arial";
      ctx.fillStyle = "#000000";
      ctx.textAlign = "center";
      
      const title = iframe.contentDocument.title || 'HTML Document';
      ctx.fillText(title, ctx.canvas.width / 2, 40);
      
      // Try to draw some representation of the content
      const bodyText = iframe.contentDocument.body?.textContent || '';
      const lines = wrapText(ctx, bodyText.substring(0, 500), ctx.canvas.width - 40, 20);
      
      lines.forEach((line, i) => {
        ctx.fillText(line, ctx.canvas.width / 2, 80 + (i * 24));
      });
    } else {
      // Draw unavailable message
      ctx.font = "16px Arial";
      ctx.fillStyle = "#555555";
      ctx.textAlign = "center";
      ctx.fillText("Preview available but content cannot be captured", ctx.canvas.width / 2, ctx.canvas.height / 2);
    }
  } catch (e) {
    console.error('Error in basic iframe capture:', e);
    // Draw error message
    ctx.font = "16px Arial";
    ctx.fillStyle = "#ff0000";
    ctx.textAlign = "center";
    ctx.fillText("Unable to capture content", ctx.canvas.width / 2, ctx.canvas.height / 2);
  }
};

/**
 * Simple HTML to canvas renderer (inspired by html2canvas)
 * @param {HTMLElement} element - Element to render
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} targetWidth - Target width
 * @param {number} targetHeight - Target height
 */
const html2canvas = (element, ctx, targetWidth, targetHeight) => {
  if (!element) return;
  
  const drawElement = (el, x = 0, y = 0, width = targetWidth) => {
    try {
      // Get computed style
      const style = window.getComputedStyle(el);
      
      // Background color
      if (style.backgroundColor && style.backgroundColor !== 'rgba(0, 0, 0, 0)') {
        ctx.fillStyle = style.backgroundColor;
        
        // Get dimensions
        const elWidth = parseInt(style.width) || el.offsetWidth;
        const elHeight = parseInt(style.height) || el.offsetHeight;
        
        ctx.fillRect(x, y, elWidth, elHeight);
      }
      
      // Text content
      if (el.childNodes.length === 1 && el.childNodes[0].nodeType === Node.TEXT_NODE) {
        const text = el.textContent.trim();
        if (text) {
          ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
          ctx.fillStyle = style.color;
          ctx.fillText(text, x + 5, y + parseInt(style.fontSize));
        }
      }
      
      // Process children
      let currentY = y;
      Array.from(el.children).forEach(child => {
        // Simple layout algorithm
        if (style.display === 'block' || style.display === 'flex') {
          currentY += child.offsetHeight;
          drawElement(child, x, currentY);
        } else {
          drawElement(child, x + child.offsetLeft, y + child.offsetTop);
        }
      });
    } catch (e) {
      console.error('Error in html2canvas:', e);
    }
  };
  
  // Start drawing from the root element
  drawElement(element);
};

/**
 * Wrap text for canvas rendering
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {string} text - Text to wrap
 * @param {number} maxWidth - Maximum width
 * @param {number} lineHeight - Line height
 * @returns {string[]} - Array of wrapped lines
 */
const wrapText = (ctx, text, maxWidth, lineHeight) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + " " + word).width;
    if (width < maxWidth) {
      currentLine += " " + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

/**
 * Render elements to canvas
 * @param {HTMLElement} node - The element to render
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} x - X position offset
 * @param {number} y - Y position offset
 */
const renderElements = (node, ctx, x = 0, y = 0) => {
  // Skip if node is not an element
  if (!(node instanceof Element)) return;
  
  // Get computed style
  const styles = window.getComputedStyle(node);
  
  // Handle background color
  if (styles.backgroundColor && styles.backgroundColor !== 'rgba(0, 0, 0, 0)') {
    ctx.fillStyle = styles.backgroundColor;
    ctx.fillRect(x, y, node.offsetWidth, node.offsetHeight);
  }
  
  // Handle borders
  if (styles.borderWidth && parseInt(styles.borderWidth) > 0) {
    ctx.strokeStyle = styles.borderColor;
    ctx.lineWidth = parseInt(styles.borderWidth);
    ctx.strokeRect(x, y, node.offsetWidth, node.offsetHeight);
  }
  
  // Handle text nodes
  if (node.childNodes.length === 1 && node.childNodes[0].nodeType === Node.TEXT_NODE) {
    ctx.font = `${styles.fontWeight} ${styles.fontSize} ${styles.fontFamily}`;
    ctx.fillStyle = styles.color;
    ctx.textBaseline = 'top';
    ctx.fillText(node.textContent, x + parseInt(styles.paddingLeft), y + parseInt(styles.paddingTop));
  }
  
  // Recursively render children
  Array.from(node.children).forEach(child => {
    const childX = x + child.offsetLeft;
    const childY = y + child.offsetTop;
    renderElements(child, ctx, childX, childY);
  });
};

/**
 * Fallback rendering if capturing fails
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {Object} settings - Capture settings
 * @param {Function} resolve - Promise resolve function
 */
const fallbackRendering = (ctx, width, height, canvas, settings, resolve) => {
  // Draw fallback message
  ctx.fillStyle = "white";
  ctx.fillRect(0, 0, width, height);
  ctx.font = "16px Arial";
  ctx.fillStyle = "black";
  ctx.textAlign = "center";
  ctx.fillText("Preview rendering not available", width/2, height/2);
  
  // Resolve with fallback
  const fallbackDataUrl = canvas.toDataURL();
  resolve(fallbackDataUrl);
};

/**
 * Resolve with data URL based on format
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {Object} settings - Capture settings
 * @param {Function} resolve - Promise resolve function
 */
const resolveWithDataURL = (canvas, settings, resolve) => {
  let mimeType = 'image/png';
  let outputQuality = undefined;
  
  switch (settings.format.toLowerCase()) {
    case 'jpeg':
      mimeType = 'image/jpeg';
      outputQuality = settings.quality;
      break;
    case 'webp':
      mimeType = 'image/webp';
      outputQuality = settings.quality;
      break;
    case 'png':
    default:
      mimeType = 'image/png';
      break;
  }
  
  try {
    const dataUrl = canvas.toDataURL(mimeType, outputQuality);
    resolve(dataUrl);
  } catch (err) {
    // Fallback if toDataURL fails
    fallbackRendering(
      canvas.getContext('2d'), 
      canvas.width, 
      canvas.height, 
      canvas, 
      settings, 
      resolve
    );
  }
};

/**
 * Extracts content from HTML string to be properly rendered in the preview
 * @param {string} html - HTML content
 * @returns {string} - Sanitized HTML content
 */
export const extractHtmlContent = (html) => {
  // Check if this is a full HTML document
  if (html.includes('<!DOCTYPE') || html.includes('<html') || html.includes('<head')) {
    // Create a DOMParser to parse the HTML string
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Create a div to hold the content
    const contentDiv = document.createElement('div');
    contentDiv.style.maxWidth = '100%';
    contentDiv.style.wordWrap = 'break-word';
    contentDiv.style.overflow = 'hidden';
    
    // If the HTML has a body tag, extract just the body content
    if (doc.body) {
      // Clone body children to the content div
      Array.from(doc.body.children).forEach(child => {
        const clonedChild = child.cloneNode(true);
        // Ensure images and other elements don't overflow
        if (clonedChild.tagName === 'IMG') {
          clonedChild.style.maxWidth = '100%';
          clonedChild.style.height = 'auto';
        }
        contentDiv.appendChild(clonedChild);
      });
      return contentDiv.innerHTML;
    }
  }
  
  // If it's not a full HTML document, return the original HTML
  return html;
};

/**
 * Alternative method to capture using rasterizeHTML library (external dependency)
 * Note: This is not implemented here but would be an alternative approach
 */
export const captureWithRasterizeHtml = (element, options) => {
  // This would use the rasterizeHTML library which is more robust
  // but requires an external dependency
  console.warn('rasterizeHTML method not implemented - external dependency required');
}; 